import { t } from '@/utils/i18n'
import { useMount, useSetState } from 'ahooks'
import { Card, Popover, Badge } from 'antd-mobile'
import { useNavigate } from 'react-router'
import { useCorpInfoStore } from '@/stores'
import Tabbar from '@/components/Tabbar/Tabbar'
import Icon from '@/components/Icon/Icon'
import styles from './index.module.less'
import { routesMap, image, apis, utils, common } from '@/utils'
import defaultAvatar from '@/assets/img/default/corp_avatar.png'
import editIcon from '@/assets/img/icon/edit.png'
import settingIcon from '@/assets/img/icon/setting.png'
import afterSaleIcon from '@/assets/img/my-center/after-sale.png'
import warehouseProcessingCountIcon from '@/assets/img/my-center/warehouse-processing-count.png'
import toCodIcon from '@/assets/img/my-center/to-cod.png'
import toConfirmIcon from '@/assets/img/my-center/to-confirm.png'
import toDeliveryIcon from '@/assets/img/my-center/to-delivery.png'
import { PfUser, initUser } from '../types'
import { Action } from 'antd-mobile/es/components/popover'
import { QueryType } from '@/constant/order'

const { getAbsoluteUrl, getSimpleUrl } = image
const { to } = utils
const { orderApi } = apis

function MyCenter() {
  const { corpInfo } = useCorpInfoStore()
  const navigate = useNavigate()
  const [userInfo, setUserInfo] = useSetState<PfUser>(initUser)
  const { pfUser, accountId, corpId } = corpInfo
  const [countStats, setCountStats] = useSetState<{
    toConfirmCount: number | null // 待确认单数
    toPickUpCount: number | null // 待自提单数
    toShippingCount: number | null // 采购中
    toDeliveryCount: number | null // 待配送
    deliveringCount: number | null // 配送中单数
    overseasShippingCount: number | null // 跨境运输中单数
    returnOrderCount: number | null // 退换单
    warehouseProcessingCount: number | null // 仓库处理中(仓库分拣中)
  }>({
    toConfirmCount: null,
    toPickUpCount: null,
    toShippingCount: null,
    toDeliveryCount: null,
    deliveringCount: null,
    overseasShippingCount: null,
    returnOrderCount: null,
    warehouseProcessingCount: null,
  })

  const actions: Action[] = [
    { key: 'langSetting', text: t('多语言') },
    { key: 'logout', text: t('退出登录') },
  ]

  useMount(() => {
    getPfUserDetail()
    getOrderCountStats()
  })

  // 获取用户信息
  async function getPfUserDetail() {
    const { name, avatar, companyName, email, phone } = pfUser || {}
    setUserInfo({
      name,
      avatar: getAbsoluteUrl(avatar),
      companyName,
      email,
      phone,
    })
  }

  async function getOrderCountStats() {
    const [err, res] = await to(
      orderApi.getOrderCountStats({
        corpId,
        customerAccountId: accountId,
      }),
    )

    if (err || !res) {
      return
    }
    const {
      toConfirmCount,
      toPickupCount,
      toShippingCount,
      toDeliveryCount,
      deliveringCount,
      overseasShippingCount,
      returnOrderCount,
      warehouseProcessingCount,
    } = res.data || {}

    setCountStats({
      toConfirmCount: toConfirmCount || null,
      toPickUpCount: toPickupCount || null,
      toShippingCount: toShippingCount || null,
      toDeliveryCount: toDeliveryCount || null,
      deliveringCount: deliveringCount || null,
      overseasShippingCount: overseasShippingCount || null,
      returnOrderCount: returnOrderCount || null,
      warehouseProcessingCount: warehouseProcessingCount || null,
    })
  }

  function onProfileClick() {
    if (accountId) {
      onPersonInfoEdit()
    } else {
      onGotoLogin()
    }
  }

  function onPersonInfoEdit() {
    navigate(routesMap.MyEdit)
  }

  function onGotoLogin() {
    common.logout()
  }

  function onGotoOrder(status: any = '') {
    navigate(routesMap.OrderList, {
      state: {
        orderStatus: status,
      },
    })
  }

  function onSettingAction(action: Action) {
    if (action.key === 'logout') {
      common.logout()
    } else if (action.key === 'langSetting') {
      navigate(routesMap.LangSetting)
    }
  }

  return (
    <div className={styles['my-center']}>
      <div className={styles['profile-box']}>
        <div className={styles['profile-info']} onClick={onProfileClick}>
          <img
            className={styles['avatar-cell']}
            src={getSimpleUrl(userInfo.avatar) || defaultAvatar}
          />

          <div className={styles['user-info']}>
            {accountId && (
              <>
                <div className={styles['my-name']}>{userInfo.name}</div>
                <div className={styles['my-info']}>
                  <div>{t('我的资料')}</div>
                  <img src={editIcon} className={styles['edit-pen']} />
                </div>
              </>
            )}

            {!accountId && (
              <div className={styles['my-name']}>{t('点击登录')}</div>
            )}
          </div>
        </div>

        <Popover.Menu
          actions={actions}
          placement="bottom-start"
          onAction={onSettingAction}
          trigger="click"
        >
          <img src={settingIcon} className={styles['setting-icon']} />
        </Popover.Menu>
      </div>

      <Card
        extra={
          <div
            className={styles['flex-box']}
            onClick={() => onGotoOrder(QueryType.ALL)}
          >
            <span className={styles['weak-text']}>{t('全部')}</span>
            <Icon name="arrow-right" size="26" color="#7f7f7f" />
          </div>
        }
        className={styles['card-box']}
        title={t('我的订单')}
      >
        <div className={styles['category-list']}>
          <div
            className={styles['category-item']}
            onClick={() => onGotoOrder(QueryType.TO_CONFIRM)}
          >
            <Badge content={countStats.toConfirmCount}>
              <img
                className={styles['category-item-icon']}
                src={toConfirmIcon}
              />
            </Badge>
            <div className={styles['category-item-name']}>{t('待确认')}</div>
          </div>

          <div
            className={styles['category-item']}
            onClick={() => onGotoOrder(QueryType.WAREHOUSE_SORTING)}
          >
            <Badge content={countStats.warehouseProcessingCount}>
              <img
                className={styles['category-item-icon']}
                src={warehouseProcessingCountIcon}
              />
            </Badge>
            <div className={styles['category-item-name']}>{t('分拣中')}</div>
          </div>

          <div
            className={styles['category-item']}
            onClick={() => onGotoOrder(QueryType.TO_DELIVERY)}
          >
            <Badge content={countStats.toDeliveryCount}>
              <img
                className={styles['category-item-icon']}
                src={toDeliveryIcon}
              />
              {/* <img className={styles['category-item-icon']} src={toCodIcon} /> */}
            </Badge>
            <div className={styles['category-item-name']}>{t('待配送')}</div>
          </div>
          <div
            className={styles['category-item']}
            onClick={() => onGotoOrder(QueryType.TO_PICKUP)}
          >
            <Badge content={countStats.toPickUpCount}>
              <img className={styles['category-item-icon']} src={toCodIcon} />
            </Badge>
            <div className={styles['category-item-name']}>{t('待自提')}</div>
          </div>
          {/* <div className={styles['category-item']} onClick={() => onGotoOrder(QueryType.COMPLETED)}>
            <img className={styles['category-item-icon']} src={completeIcon} />
            <div className={styles['category-item-name']}>{t('已完成')}</div>
          </div> */}
          <div
            className={styles['category-item']}
            onClick={() => onGotoOrder(QueryType.RETURNS)}
          >
            <Badge content={countStats.returnOrderCount}>
              <img
                className={styles['category-item-icon']}
                src={afterSaleIcon}
              />
            </Badge>
            <div className={styles['category-item-name']}>{t('退换单')}</div>
          </div>
        </div>
      </Card>

      <Tabbar />
    </div>
  )
}

export default MyCenter
